/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { liftLoadedState, mapLoadedUnpack } from "models/loaded";
import { LoadedContent } from "src/components/common/loading/loadedContent";
import { useTenantTranslation } from "hooks/formatters";
import { MixDetailsView } from "./MixDetailsView";
import {
  useChemicalElements,
  useChemistryGroups,
  useSteelGrades,
  useMaterials,
} from "contexts/search/SearchContextProvider";
import { PlanId } from "components/common/boundary/PlanId";
import { createMapping } from "helpers";
import { Period } from "hooks/periodIndex";
import { DrawerLayout } from "../layout";
import { usePlan } from "contexts/plan";

interface BaseProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface MixProps {
  mix: number;
  planId: PlanId;
  group: number;
  period: Period;
  multipleMixes: boolean;
}

type Props = BaseProps & MixProps;

export const MixDetailsPanel = ({
  group,
  open,
  setOpen,
  planId,
  mix,
  period,
  multipleMixes,
}: Props) => {
  const { t } = useTenantTranslation();

  const chemistryGroup = mapLoadedUnpack(
    useChemistryGroups(),
    (groups) => groups.byId[group] ?? null
  );

  if (chemistryGroup === null) {
    /* Sometimes, the data grid's data model lags behind the actual data,
      causing IDs of chef groups to be passed to the render function when
      the renderer has already switched to looking up product groups.  This
      can momentarily cause the product group to not exist in the lookup,
      which can be solved by having this fail gracefully */
    return null;
  }

  return (
    <DrawerLayout
      title={t("mixDetails")}
      open={open}
      doClose={() => {
        setOpen(false);
      }}
      width={1200}
    >
      <LoadedMixDetails
        planId={planId}
        mix={mix}
        group={group}
        period={period}
        multipleMixes={multipleMixes}
      />
    </DrawerLayout>
  );
};

type LoadedMixDetailsProps = MixProps;

const LoadedMixDetails = ({
  planId,
  mix,
  group,
  period,
  multipleMixes,
}: LoadedMixDetailsProps) => {
  const chemistryGroups = useChemistryGroups();
  const chemicalElements = useChemicalElements();
  const steelGrades = useSteelGrades();
  const materials = useMaterials();

  const { plan } = usePlan(planId);

  const dependencies = liftLoadedState({
    chemistryGroups,
    chemicalElements,
    steelGrades,
    materials,
    plan,
  });

  return (
    <LoadedContent data={dependencies}>
      {({
        plan,
        chemistryGroups,
        chemicalElements,
        steelGrades: steelGradesSearch,
        materials,
      }) => {
        const chemistryGroup = chemistryGroups.byId[group]!;
        return (
          <MixDetailsView
            chemicalConstraints={chemistryGroup.chemical_constraints}
            chemistryGroupId={chemistryGroup.id}
            chemistryGroupName={chemistryGroup.name}
            maximumFailureRate={chemistryGroup.max_failure_rate}
            defaultTargetNumberOfBaskets={
              chemistryGroup.default_target_num_baskets
            }
            steelGrades={chemistryGroup.steel_grades.map(
              ({ id }) => steelGradesSearch.byId[id]!
            )}
            period={period}
            mix={mix}
            summary={plan.summary}
            elements={createMapping(
              chemicalElements.byIndex,
              (element) => element.id
            )}
            planId={plan.id as PlanId}
            multipleMixes={multipleMixes}
            materials={materials.byIndex}
            externalRecipes={plan.external_recipes ?? null}
          />
        );
      }}
    </LoadedContent>
  );
};
